<template>

  <div class="jours-non-ouvrables-container">
    <Header title="Jours non-ouvrables" />
    <div class="jours-view">
      <div class="container full-width">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Jours non-ouvrables</h1>
          <div class="header-actions">
            <button class="btn btn-primary" @click="openAddJourModal">
              <span class="material-icons">add</span>
              Nouveau jour
            </button>
          </div>
        </div>

        <!-- Section de recherche -->
        <div class="search-section">
          <div class="search-input">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher un jour..." 
              @input="filterJours"
              aria-label="Rechercher un jour non-ouvrable"
            />
          </div>
        </div>

        <!-- Liste des jours non-ouvrables -->
        <div class="content-container">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des jours non-ouvrables...</p>
          </div>
          
          <div v-else-if="filteredJours.length === 0" class="no-results">
            <span class="material-icons">search_off</span>
            <p v-if="searchQuery">Aucun jour trouvé pour "{{ searchQuery }}"</p>
            <p v-else>Aucun jour non-ouvrable disponible. Veuillez en ajouter un.</p>
          </div>

          <div v-else class="jours-list">
            <div
              v-for="jour in filteredJours"
              :key="jour.id"
              class="jour-item"
              :class="{ 'selected': selectedJour && selectedJour.id === jour.id }"
              @click="selectJour(jour)"
            >
              <div class="jour-info">
                <span class="jour-icon material-icons">event_busy</span>
                <div class="jour-details">
                  <h3>{{ jour.name }}</h3>
                  <p>
                    <span class="date-info">{{ formatJourDate(jour) }}</span>
                    <span v-if="jour.est_recurrent" class="recurrent-tag">Récurrent</span>
                  </p>
                </div>
              </div>
              <div class="jour-actions">
                <button class="btn-icon" @click.stop="editJour(jour)">
                  <span class="material-icons">edit</span>
                </button>
                <button class="btn-icon" @click.stop="confirmDeleteJour(jour)">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails du jour sélectionné -->
        <div v-if="selectedJour" class="jour-details-panel enhanced-panel">
          <div class="panel-header">
            <h3>Détails du jour non-ouvrable</h3>
            <button class="btn-close" @click="selectedJour = null">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div class="panel-content enhanced-content">           
            <div class="detail-row">
              <span class="detail-icon material-icons">event</span>
              <span class="detail-label">DATE</span>
              <span class="detail-value">{{ formatJourDate(selectedJour) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-icon material-icons">repeat</span>
              <span class="detail-label">RÉCURRENT</span>
              <span class="detail-value">
                <span class="status-badge" :class="selectedJour.est_recurrent ? 'active' : 'inactive'">
                  {{ selectedJour.est_recurrent ? 'Oui' : 'Non' }}
                </span>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-icon material-icons">description</span>
              <span class="detail-label">DESCRIPTION</span>
              <span class="detail-value">{{ selectedJour.description || 'Aucune description' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-icon material-icons">add_circle</span>
              <span class="detail-label">CRÉÉ LE</span>
              <span class="detail-value">{{ formatDate(selectedJour.createdAt) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-icon material-icons">edit</span>
              <span class="detail-label">MODIFIÉ LE</span>
              <span class="detail-value">{{ formatDate(selectedJour.updatedAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay et modales -->
    <div v-if="showJourModal || showConfirmModal" class="modal-container">
      <div class="modal-overlay" @click="closeAllModals"></div>
      
      <!-- Modal pour l'ajout/modification d'un jour non-ouvrable -->
      <div v-if="showJourModal" class="jour-modal modal-centered">
      <div class="modal-header">
        <div class="modal-title">
          <span class="material-icons modal-icon">{{ currentAction === 'create' ? 'add_circle' : 'edit' }}</span>
          <h3>{{ currentAction === 'create' ? 'Ajouter un jour non-ouvrable' : 'Modifier le jour non-ouvrable' }}</h3>
        </div>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body scrollable-form">
        <form @submit.prevent="saveJour" id="jourForm">
          <div class="form-group">
            <label for="name">Nom du jour non-ouvrable<span class="required">*</span></label>
            <div class="input-with-icon">
              <span class="material-icons input-icon">event_note</span>
              <input 
                type="text" 
                id="name" 
                v-model="formData.name" 
                required 
                :class="{ 'error': formErrors.name }"
                placeholder="Ex: Jour de l'An"
              />
            </div>
            <span class="error-message" v-if="formErrors.name">{{ formErrors.name }}</span>
          </div>
          <div class="form-group">
            <label for="date">Date<span class="required" v-if="!formData.est_recurrent">*</span></label>
            <div class="input-with-icon">
              <span class="material-icons input-icon">calendar_today</span>
              <input 
                type="date" 
                id="date" 
                v-model="formData.date" 
                :required="!formData.est_recurrent" 
                :class="{ 'error': formErrors.date }"
              />
            </div>
            <span class="error-message" v-if="formErrors.date">{{ formErrors.date }}</span>
          </div>
          <div class="form-group checkbox-group">
            <div class="checkbox-container">
              <input 
                type="checkbox" 
                id="est_recurrent" 
                v-model="formData.est_recurrent"
                class="styled-checkbox"
                @change="toggleRecurrentFields"
              />
              <span class="checkmark"></span>
              <label for="est_recurrent">Récurrent (se répète chaque année)</label>
            </div>
          </div>
          
          <div v-if="formData.est_recurrent" class="recurrent-fields">
            <div class="form-row">
              <div class="form-group half-width">
                <label for="mois">Mois<span class="required">*</span></label>
                <div class="input-with-icon">
                  <span class="material-icons input-icon">date_range</span>
                  <select 
                    id="mois" 
                    v-model="formData.mois" 
                    required 
                    :class="{ 'error': formErrors.mois }"
                  >
                    <option value="" disabled>Sélectionnez un mois</option>
                    <option v-for="i in 12" :key="i" :value="i">{{ getMonthName(i) }}</option>
                  </select>
                </div>
                <span class="error-message" v-if="formErrors.mois">{{ formErrors.mois }}</span>
              </div>
              
              <div class="form-group half-width">
                <label for="jour">Jour<span class="required">*</span></label>
                <div class="input-with-icon">
                  <span class="material-icons input-icon">today</span>
                  <select 
                    id="jour" 
                    v-model="formData.jour" 
                    required 
                    :class="{ 'error': formErrors.jour }"
                  >
                    <option value="" disabled>Sélectionnez un jour</option>
                    <option v-for="i in getDaysInMonth(formData.mois)" :key="i" :value="i">{{ i }}</option>
                  </select>
                </div>
                <span class="error-message" v-if="formErrors.jour">{{ formErrors.jour }}</span>
              </div>
            </div>
          </div>
          <div class="form-group">
            <label for="description">Description</label>
            <div class="input-with-icon textarea-container">
              <span class="material-icons input-icon">description</span>
              <textarea 
                id="description" 
                v-model="formData.description" 
                rows="3"
                placeholder="Informations complémentaires..."
              ></textarea>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">
          <span class="material-icons">close</span>
          Annuler
        </button>
        <button 
          class="btn btn-primary" 
          form="jourForm" 
          :disabled="savingJour"
          :class="{ 'loading': savingJour }"
        >
          <span v-if="!savingJour" class="material-icons">{{ currentAction === 'create' ? 'add' : 'save' }}</span>
          <div v-if="savingJour" class="btn-spinner"></div>
          {{ savingJour ? 'Traitement en cours...' : (currentAction === 'create' ? 'Ajouter' : 'Enregistrer') }}
        </button>
      </div>
      </div>

      <!-- Modal de confirmation de suppression -->
      <div v-if="showConfirmModal" class="confirm-modal modal-centered">
        <div class="modal-header">
          <div class="modal-title">
            <span class="material-icons modal-icon warning">warning</span>
            <h3>Confirmer la suppression</h3>
          </div>
          <button class="btn-close" @click="closeConfirmModal">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body confirm-body">
          <div class="warning-icon-container">
            <span class="material-icons warning-icon">delete_forever</span>
          </div>
          <p>Êtes-vous sûr de vouloir supprimer le jour non-ouvrable <strong>"{{ jourToDelete?.description }}"</strong> ?</p>
          <p class="warning">Cette action est irréversible et peut affecter les calculs de congés.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeConfirmModal">
            <span class="material-icons">arrow_back</span>
            Annuler
          </button>
          <button 
            class="btn btn-danger" 
            @click="deleteJour"
            :disabled="deletingJour"
            :class="{ 'loading': deletingJour }"
          >
            <span v-if="!deletingJour" class="material-icons">delete</span>
            <div v-if="deletingJour" class="btn-spinner"></div>
            {{ deletingJour ? 'Suppression...' : 'Supprimer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Notification toast -->
    <div 
      v-if="notification.show" 
      class="notification" 
      :class="notification.type"
    >
      <div class="notification-icon">
        <span class="material-icons">
          {{ notification.type === 'success' ? 'check_circle' : 
             notification.type === 'error' ? 'error' : 
             notification.type === 'info' ? 'info' : 'warning' }}
        </span>
      </div>
      <div class="notification-content">
        <h4>{{ notification.title }}</h4>
        <p>{{ notification.message }}</p>
      </div>
      <button class="notification-close" @click="notification.show = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue';
import api from '../services/api';
import Header from '../components/Header.vue';

const jours = ref([]);
const filteredJours = ref([]);
const selectedJour = ref(null);
const loading = ref(false);
const savingJour = ref(false);
const deletingJour = ref(false);
const searchQuery = ref('');

// Modals
const jourModal = ref(null);
const confirmDeleteModal = ref(null);
const showJourModal = ref(false);
const showConfirmModal = ref(false);

// État du formulaire
const formData = reactive({
  id: null,
  name: '',
  date: '',
  est_recurrent: false,
  description: '',
  mois: '',
  jour: ''
});
const formErrors = reactive({
  name: '',
  date: '',
  mois: '',
  jour: '',
  description: ''
});

// État de l'action courante (create, update, delete)
const currentAction = ref('create');
const jourToDelete = ref(null);

// Notification
const notification = reactive({
  show: false,
  type: 'info',
  title: '',
  message: '',
  duration: 5000
});

onMounted(async () => {
  await loadJours();
});

async function loadJours() {
  loading.value = true;
  try {
    const response = await api.getJoursNonOuvrables();
    if (response.data && response.data.success) {
      jours.value = response.data.data || [];
      filteredJours.value = [...jours.value];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des jours non-ouvrables:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les jours non-ouvrables.');
  } finally {
    loading.value = false;
  }
}

function filterJours() {
  if (!searchQuery.value.trim()) {
    filteredJours.value = [...jours.value];
    return;
  }
  
  const query = searchQuery.value.toLowerCase();
  filteredJours.value = jours.value.filter(jour => {
    const nomMatch = jour.name.toLowerCase().includes(query);
    const dateMatch = formatDate(jour.date).toLowerCase().includes(query);
    const descMatch = jour.description ? jour.description.toLowerCase().includes(query) : false;
    
    return nomMatch || dateMatch || descMatch;
  });
}

function selectJour(jour) {
  selectedJour.value = jour;
}

function openAddJourModal() {
  // Fermer toute autre modale ouverte d'abord
  if (showConfirmModal.value) {
    closeConfirmModal();
  }
  
  resetForm();
  
  // Initialiser la date à aujourd'hui
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  formData.date = `${yyyy}-${mm}-${dd}`;
  
  currentAction.value = 'create';
  showJourModal.value = true;
}

function editJour(jour) {
  // Fermer toute autre modale ouverte d'abord
  if (showConfirmModal.value) {
    closeConfirmModal();
  }
  
  currentAction.value = 'update';
  
  formData.id = jour.id;
  formData.name = jour.name;
  formData.date = jour.date?.substring(0, 10) || ''; // Format YYYY-MM-DD
  formData.est_recurrent = jour.est_recurrent;
  formData.description = jour.description || '';
  
  if (jour.est_recurrent && jour.mois && jour.jour) {
    formData.mois = jour.mois;
    formData.jour = jour.jour;
  }
  
  showJourModal.value = true;
}

function confirmDeleteJour(jour) {
  // Fermer toute autre modale ouverte d'abord
  if (showJourModal.value) {
    closeModal();
  }
  
  jourToDelete.value = jour;
  showConfirmModal.value = true;
}

function closeModal() {
  showJourModal.value = false;
  resetForm();
}

function closeAllModals() {
  showJourModal.value = false;
  showConfirmModal.value = false;
  resetForm();
}

function closeConfirmModal() {
  showConfirmModal.value = false;
  jourToDelete.value = null;
}

function resetForm() {
  formData.id = null;
  formData.name = '';
  formData.date = '';
  formData.est_recurrent = false;
  formData.description = '';
  formData.mois = '';
  formData.jour = '';
  
  formErrors.name = '';
  formErrors.date = '';
  formErrors.mois = '';
  formErrors.jour = '';
  formErrors.description = '';
}

async function saveJour() {
  if (!validateForm()) return;
  
  savingJour.value = true;
  
  try {
    const jourData = {
      name: formData.name,
      description: formData.description,
      est_recurrent: formData.est_recurrent
    };
    
    // Si c'est récurrent, ajouter mois et jour
    if (formData.est_recurrent) {
      jourData.mois = parseInt(formData.mois);
      jourData.jour = parseInt(formData.jour);
    } else {
      // Sinon, ajouter la date
      jourData.date = formData.date;
    }
    
    let response;
    
    if (currentAction.value === 'create') {
      response = await api.createJourNonOuvrable(jourData);
      showNotification('success', 'Succès', 'Jour non-ouvrable ajouté avec succès');
    } else {
      response = await api.updateJourNonOuvrable(formData.id, jourData);
      showNotification('success', 'Succès', 'Jour non-ouvrable mis à jour avec succès');
    }
    
    if (response.data) {
      await loadJours();
      closeModal();
    }
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement du jour non-ouvrable:', error);
    showNotification('error', 'Erreur', `Une erreur est survenue: ${error.message}`);
  } finally {
    savingJour.value = false;
  }
}

async function deleteJour() {
  if (!jourToDelete.value) return;
  
  deletingJour.value = true;
  
  try {
    const response = await api.deleteJourNonOuvrable(jourToDelete.value.id);
    if (response.data && response.data.success) {
      showNotification('success', 'Suppression', 'Jour non-ouvrable supprimé avec succès');
      
      if (selectedJour.value && selectedJour.value.id === jourToDelete.value.id) {
        selectedJour.value = null;
      }
      
      await loadJours();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression du jour non-ouvrable:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer le jour non-ouvrable: ${error.message}`);
  } finally {
    deletingJour.value = false;
  }
  
  closeConfirmModal();
}

function validateForm() {
  let isValid = true;
  
  // Réinitialiser les erreurs
  formErrors.name = '';
  formErrors.date = '';
  formErrors.mois = '';
  formErrors.jour = '';
  formErrors.description = '';
  
  // Valider le nom
  if (!formData.name.trim()) {
    formErrors.name = 'Le nom est obligatoire';
    isValid = false;
  } else if (formData.name.length > 255) {
    formErrors.name = 'Le nom ne peut pas dépasser 255 caractères';
    isValid = false;
  } else {
    formErrors.name = '';
  }
  
  // Valider la description
  if (!formData.description.trim()) {
    formErrors.description = 'La description est obligatoire';
    isValid = false;
  } else {
    formErrors.description = '';
  }
  
  // Validation différente selon que c'est récurrent ou non
  if (formData.est_recurrent) {
    // Validation du mois
    if (!formData.mois) {
      formErrors.mois = 'Le mois est obligatoire';
      isValid = false;
    } else {
      formErrors.mois = '';
    }
    
    // Validation du jour
    if (!formData.jour) {
      formErrors.jour = 'Le jour est obligatoire';
      isValid = false;
    } else {
      formErrors.jour = '';
    }
    
    // Réinitialiser l'erreur de date car elle n'est pas obligatoire en mode récurrent
    formErrors.date = '';
  } else {
    // Validation de la date
    if (!formData.date) {
      formErrors.date = 'La date est obligatoire';
      isValid = false;
    } else {
      formErrors.date = '';
    }
    
    // Réinitialiser les erreurs de mois et jour car ils ne sont pas obligatoires en mode non récurrent
    formErrors.mois = '';
    formErrors.jour = '';
  }
  
  return isValid;
}

function showNotification(type, title, message) {
  notification.show = true;
  notification.type = type;
  notification.title = title;
  notification.message = message;
  
  setTimeout(() => {
    notification.show = false;
  }, notification.duration);
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
}

function formatJourDate(jour) {
  if (jour.est_recurrent) {
    // Pour les jours récurrents, utiliser les champs mois et jour
    if (!jour.jour || !jour.mois) return 'N/A';
    
    const jourStr = jour.jour.toString();
    const moisIndex = parseInt(jour.mois) - 1; // Les mois sont indexés de 0 à 11
    const moisNom = getMonthName(jour.mois);
    return `${jourStr} ${moisNom.toLowerCase()}`;
  } else {
    // Pour les jours non récurrents, utiliser le champ date
    if (!jour.date) return 'N/A';
    
    const date = new Date(jour.date);
    const jourNum = date.getDate();
    const mois = getMonthName(date.getMonth() + 1);
    return `${jourNum} ${mois.toLowerCase()}`;
  }
}
// Fonctions utilitaires pour les jours et mois
function toggleRecurrentFields() {
  // Si on active la récurrence et qu'une date est déjà sélectionnée,
  // on peut extraire le mois et le jour de cette date
  if (formData.est_recurrent && formData.date) {
    const dateObj = new Date(formData.date);
    formData.mois = dateObj.getMonth() + 1; // getMonth() retourne 0-11
    formData.jour = dateObj.getDate();
  } else if (!formData.est_recurrent) {
    // Si on désactive la récurrence, on réinitialise les champs mois et jour
    formData.mois = '';
    formData.jour = '';
  }
  
  // Réinitialiser les erreurs de validation
  if (formData.est_recurrent) {
    formErrors.date = '';
  } else {
    formErrors.mois = '';
    formErrors.jour = '';
  }
}

function getMonthName(monthNumber) {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return months[monthNumber - 1]; // -1 car les mois sont indexés de 0 à 11
}

function getDaysInMonth(month) {
  if (!month) return 31;
  
  // Mois avec 31 jours
  if ([1, 3, 5, 7, 8, 10, 12].includes(parseInt(month))) {
    return 31;
  }
  // Février (on prend 29 pour les années bissextiles)
  else if (parseInt(month) === 2) {
    return 29;
  }
  // Mois avec 30 jours
  else {
    return 30;
  }
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.jours-view {
  padding: 20px;
  width: 100%;
  margin: 0 auto;
}

.container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.container.full-width {
  max-width: 100%;
  width: 100%;
}

@media (min-width: 992px) {
  .container {
    grid-template-columns: 2fr 1fr;
  }
  
  .view-header, .search-section, .loading-indicator, .no-results {
    grid-column: span 2;
  }
}

/* En-tête */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.view-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-deep);
  text-decoration: none;
  font-weight: 500;
}

.back-button .material-icons {
  margin-right: 5px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Section de recherche */
.search-section {
  margin-bottom: 20px;
}

.search-input {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px 15px;
}

.search-input .material-icons {
  color: #777;
  margin-right: 10px;
}

.search-input input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 16px;
  outline: none;
}

/* Liste des jours non-ouvrables */
.jours-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.jour-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  border-left: 4px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.jour-item:hover {
  background-color: #f0f0f0;
}

.jour-item.selected {
  border-left-color: var(--turquoise-primary);
  background-color: var(--turquoise-pale);
}

.jour-info {
  display: flex;
  align-items: center;
}

.jour-icon {
  color: var(--turquoise-primary); /* Couleur principale turquoise */
  margin-right: 15px;
  font-size: 24px;
}

.jour-details h3 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 500;
}

.jour-details p {
  margin: 0;
  color: #777;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.date-info {
  font-weight: 500;
}

.recurrent-tag {
  background-color: #E0F7F4; /* Turquoise très clair */
  color: #26A194; /* Turquoise foncé */
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 12px;
  margin-left: 8px;
}

.jour-actions {
  display: flex;
  gap: 10px;
}

.btn-close {
  border: none;
  background: none;
  color: white;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.btn-icon {
  border: none;
  background: none;
  color: #777;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #333;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: var(--turquoise-dark);
}

/* Panneau de détails */
.jour-details-panel {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.panel-content {
  padding: 15px;
}

.detail-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 14px;
  color: #777;
  margin-bottom: 3px;
}

.field-value {
  font-size: 16px;
  color: #333;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

.status-badge.inactive {
  background-color: #f5f5f5;
  color: #757575;
}

/* Indicateur de chargement */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #30C7B5; /* Couleur principale turquoise */
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Pas de résultats */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #777;
}

.no-results .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: #A9EAE1; /* Turquoise très clair */
}

/* Modal */
.jour-modal, .confirm-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;
  margin: 0;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  z-index: 1000;
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  animation: fadeIn 0.2s ease-out;
}

.jour-modal::backdrop, .confirm-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-icon {
  color: var(--turquoise-primary);
  font-size: 24px;
}

.modal-icon.warning {
  color: var(--turquoise-primary);
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.modal-body {
  padding: 24px;
  background-color: #ffffff;
}

.scrollable-form {
  overflow-y: auto;
  max-height: calc(80vh - 130px); /* Hauteur maximale en tenant compte des en-têtes et pieds de page */
  scrollbar-width: thin;
  scrollbar-color: var(--turquoise-deep) #f0f0f0;
}

.confirm-body {
  text-align: center;
}

.warning-icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.warning-icon {
  font-size: 48px;
  color: #FF9800;
  background-color: rgba(255, 152, 0, 0.1);
  padding: 16px;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
}

.modal-footer {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
}

.btn-close:hover {
  background-color: #e5e7eb;
  color: #1f2937;
  transform: rotate(90deg);
}

/* Formulaire */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
}

.required {
  color: #dc2626;
  margin-left: 3px;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #30C7B5; /* Couleur principale turquoise */
  font-size: 1.25rem;
  opacity: 0.7;
  transition: all 0.3s ease;
  z-index: 2;
  pointer-events: none;
  background-color: transparent;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.textarea-container .input-icon {
  top: 12px;
}

.checkbox-group {
  margin-top: 5px;
  margin-bottom: 25px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 30px;
  cursor: pointer;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #fff;
  border: 1.5px solid #6b7280;
  border-radius: 4px;
  transition: all 0.2s;
}

.checkbox-container:hover input ~ .checkmark {
  border-color: var(--turquoise-primary);
}

.checkbox-container input:checked ~ .checkmark {
  background-color: var(--turquoise-primary);
  border-color: var(--turquoise-primary);
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-group label {
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  width: auto;
}

.form-group input[type="text"],
.form-group input[type="date"],
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fff;
}

.input-with-icon input,
.input-with-icon textarea,
.input-with-icon select {
  padding-left: 40px;
  width: 100%;
  position: relative;
  z-index: 1;
  background-color: #fff;
  box-shadow: none;
  border: 1px solid #d1d5db;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #30C7B5; /* Couleur principale turquoise */
  box-shadow: 0 0 0 3px rgba(48, 199, 181, 0.2); /* Ombre turquoise */
}

.form-group:focus-within .input-icon {
  opacity: 1;
}

.form-group:focus-within label {
  color: #30C7B5; /* Couleur principale turquoise */
  font-weight: 600;
}

/* Boutons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  gap: 8px;
  font-size: 14px;
}

.btn-primary {
  background-color: #30C7B5; /* Couleur principale turquoise */
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover {
  background-color: #26A194; /* Turquoise foncé */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
  color: #1f2937;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-danger:hover {
  background-color: #dc2626;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

/* Notification */
.notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  z-index: 1000;
}

.notification.success {
  border-left: 4px solid #4CAF50;
}

.notification.error {
  border-left: 4px solid #F44336;
}

.notification.warning {
  border-left: 4px solid #FF9800;
}

.notification.info {
  border-left: 4px solid #30C7B5; /* Couleur principale turquoise */
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification.success .material-icons {
  color: #4CAF50;
}

.notification.error .material-icons {
  color: #F44336;
}

.notification.warning .material-icons {
  color: #FF9800;
}

.notification.info .material-icons {
  color: #30C7B5; /* Couleur principale turquoise */
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 600;
}

.notification-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -5px;
}

.notification-close:hover {
  color: #333;
}

.warning {
  color: #d32f2f;
  font-weight: 500;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { opacity: 0; transform: translate(-50%, -60%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
}

.jour-modal, .confirm-modal {
  animation: slideIn 0.3s ease-out;
}
.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

.half-width {
  flex: 1;
  min-width: 0;
}

.recurrent-fields {
  background-color: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  animation: fadeIn 0.3s ease-out;
}
/* Styles pour les scrollbars personnalisées */
.scrollable-form::-webkit-scrollbar {
  width: 8px;
}

.scrollable-form::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.scrollable-form::-webkit-scrollbar-thumb {
  background-color: #30C7B5; /* Couleur principale turquoise */
  border-radius: 4px;
  border: 2px solid #f0f0f0;
}

.scrollable-form::-webkit-scrollbar-thumb:hover {
  background-color: #26A194; /* Turquoise foncé */
}

/* Button spinner styles */
.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
}

.btn.loading {
  position: relative;
}

/* Enhanced details panel */
.enhanced-panel {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fdfa 100%);
  border: 1px solid rgba(48, 199, 181, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.enhanced-content {
  padding: 20px;
}

.detail-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 15px;
  padding: 12px 15px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(48, 199, 181, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.detail-row:hover {
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(48, 199, 181, 0.15);
  transform: translateY(-1px);
}

.detail-icon {
  color: var(--turquoise-primary);
  font-size: 20px;
  transition: all 0.3s ease;
}

.detail-row:hover .detail-icon {
  transform: scale(1.1);
  color: var(--turquoise-deep);
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  text-align: right;
  background: rgba(248, 250, 252, 0.8);
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
}

.detail-row:hover .detail-value {
  background: rgba(240, 253, 250, 0.9);
  border-color: rgba(48, 199, 181, 0.2);
  box-shadow: 0 1px 3px rgba(48, 199, 181, 0.1);
}
</style>
